import { Check, X, RotateCcw, Trash2, Mail, Link as LinkIcon, Heart } from "lucide-react";
import { StatusBadge, formatDate, PhotoThumb, JoinedAtEditor } from "./parts";

// One member <tr> in the admin members table: photo, tier/role/community selects,
// aliança flag, joined-at editor, status and per-state actions.
export const MemberRow = ({ m, cieesRoles, communities, handlers }) => {
  const { act, getActivationLink, remove, setTier, setJoinedAt, setCieesRole, setCommunity, resendApproval, load } = handlers;
  return (
    <tr className="border-b border-[hsl(var(--border))] last:border-0">
      <td className="px-4 py-3 align-top text-[hsl(var(--muted-foreground))]">{formatDate(m.created_at)}</td>
      <td className="px-4 py-3 align-top">
        <PhotoThumb member={m} onUploaded={load} />
      </td>
      <td className="px-4 py-3 align-top">{m.name || "—"}</td>
      <td className="px-4 py-3 align-top">{m.email}</td>
      <td className="px-4 py-3 align-top">
        <span className="text-xs px-2 py-1 bg-[hsl(var(--secondary))]">
          {m.auth_method === "google" ? "Google" : "Email"}
        </span>
      </td>
      <td className="px-4 py-3 align-top">
        <select
          value={m.tier || ""}
          onChange={(e) => setTier(m, e.target.value)}
          disabled={m.status !== "approved"}
          className="bg-white border border-[hsl(var(--border))] text-xs px-2 py-1 focus:outline-none focus:border-[hsl(var(--accent))] disabled:opacity-50"
          data-testid={`tier-select-${m.id}`}
          title={m.status !== "approved" ? "Apenas para sócios aprovados" : ""}
        >
          <option value="">— sem nível —</option>
          <option value="aderente">Aderente</option>
          <option value="efetivo">Efetivo</option>
          <option value="benemerito">Benemérito</option>
          <option value="institucional">Institucional</option>
          <option value="honorario">Honorário</option>
        </select>
      </td>
      <td className="px-4 py-3 align-top">
        <select
          value={m.ciees_role || ""}
          onChange={(e) => setCieesRole(m, e.target.value || null)}
          disabled={m.status !== "approved"}
          className="bg-white border border-[hsl(var(--border))] px-2 py-1 text-xs focus:outline-none focus:border-[hsl(var(--accent))] disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={`ciees-role-${m.id}`}
          title={m.status !== "approved" ? "Apenas para sócios aprovados" : "Função na organização"}
        >
          <option value="">— sem função —</option>
          {cieesRoles.map((r) => (
            <option key={r.key} value={r.key}>{r.label}</option>
          ))}
        </select>
      </td>
      <td className="px-4 py-3 align-top">
        <select
          value={m.community_id || ""}
          onChange={(e) => setCommunity(m, e.target.value || null)}
          disabled={m.status !== "approved"}
          className="bg-white border border-[hsl(var(--border))] px-2 py-1 text-xs focus:outline-none focus:border-[hsl(var(--accent))] disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={`community-select-${m.id}`}
          title={m.status !== "approved" ? "Apenas para sócios aprovados" : "Comunidade regional"}
        >
          <option value="">— sem comunidade —</option>
          {communities.map((c) => (
            <option key={c.slug} value={c.slug}>{c.name}</option>
          ))}
        </select>
      </td>
      <td className="px-4 py-3 align-top" data-testid={`alianca-cell-${m.id}`}>
        {m.has_alianca_amor ? (
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--accent))]" title={m.alianca_amor_display || ""}>
            <Heart className="h-3 w-3 fill-[hsl(var(--accent))] stroke-[hsl(var(--accent))]" /> Sim
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))]">
            <Heart className="h-3 w-3" /> Não
          </span>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        <JoinedAtEditor
          value={m.joined_at}
          onSave={(v) => setJoinedAt(m, v)}
          disabled={m.status !== "approved"}
          testid={`joined-at-${m.id}`}
        />
      </td>
      <td className="px-4 py-3 align-top">
        <StatusBadge status={m.status} />
      </td>
      <td className="px-4 py-3 text-right align-top">
        {m.status === "pending" && (
          <div className="inline-flex gap-2">
            <button
              onClick={() => act(m.id, "approve")}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-wide hover:bg-[#093630]"
              data-testid={`approve-${m.id}`}
            >
              <Check className="h-3 w-3" /> Aprovar
            </button>
            <button
              onClick={() => act(m.id, "reject")}
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] text-xs uppercase tracking-wide hover:text-[hsl(var(--destructive))] hover:border-[hsl(var(--destructive))]"
              data-testid={`reject-${m.id}`}
            >
              <X className="h-3 w-3" /> Rejeitar
            </button>
          </div>
        )}
        {m.status === "approved" && (
          <div className="inline-flex gap-2">
            <button
              onClick={() => getActivationLink(m)}
              className="inline-flex items-center gap-1 text-xs text-[hsl(var(--primary))] hover:underline"
              data-testid={`activation-link-${m.id}`}
              title="Gerar link manual para partilhar via WhatsApp/SMS"
            >
              <LinkIcon className="h-3.5 w-3.5" /> Link manual
            </button>
            <button
              onClick={() => resendApproval(m)}
              className="inline-flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
              data-testid={`resend-approval-${m.id}`}
              title={m.approval_email_sent_at ? `Último envio: ${new Date(m.approval_email_sent_at).toLocaleString("pt-PT")}` : "Email ainda não enviado — clique para enviar"}
            >
              <Mail className="h-3.5 w-3.5" /> {m.approval_email_sent_at ? "Re-enviar" : "Enviar"}
            </button>
            <button
              onClick={() => act(m.id, "reject")}
              className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]"
            >
              Revogar
            </button>
          </div>
        )}
        {m.status === "rejected" && (
          <div className="inline-flex gap-2">
            <button
              onClick={() => act(m.id, "reinstate")}
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-xs uppercase tracking-wide hover:bg-[hsl(var(--primary))] hover:text-white transition-colors"
              data-testid={`reinstate-${m.id}`}
              title="Voltar a colocar como pendente"
            >
              <RotateCcw className="h-3 w-3" /> Reabilitar
            </button>
            <button
              onClick={() => remove(m)}
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-[hsl(var(--destructive))] text-[hsl(var(--destructive))] text-xs uppercase tracking-wide hover:bg-[hsl(var(--destructive))] hover:text-white transition-colors"
              data-testid={`delete-${m.id}`}
              title="Apagar definitivamente"
            >
              <Trash2 className="h-3 w-3" /> Apagar
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};
